import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ApiError } from '../api/client';
import { login } from '../api/auth';
import { useAuth } from '../auth/context';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Field } from '../components/Field';
import { Input } from '../components/Input';
import { useApi } from '../api/use-api';

export function LoginPage() {
  const api = useApi();
  const auth = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await login(api, email, password);
      auth.login(res.userId, res.sessionJwt);
      void navigate('/hosts', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setError('Invalid email or password.');
      else setError('Login failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid h-screen place-items-center px-4">
      <Card variant="elevated" className="w-full max-w-sm p-8">
        <header className="mb-6 space-y-2">
          <p className="font-[var(--font-serif)] text-sm italic text-text-secondary">
            <span className="not-italic font-sans text-accent">▸ </span>
            remote.console
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-text-primary">Sign in</h1>
          <p className="text-sm text-text-secondary">to your relay account</p>
        </header>
        <form onSubmit={(e) => { void onSubmit(e); }} className="space-y-4">
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </Field>
          <Field label="Password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </Field>
          {error && <p className="text-sm text-danger" role="alert">{error}</p>}
          <Button type="submit" loading={busy} className="w-full">Sign in</Button>
        </form>
      </Card>
    </main>
  );
}
